import React from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import { FormattedDate, FormattedMessage } from '@techstyle/react-intl';

import { desktop, mobile } from '../../styles';
import { Paragraph } from '../../styles/paragraph';
import { parseDate } from '../../utils/dates';
import { Component as Stars } from '../Stars';
import { Container as StarsContainer } from '../Stars/Stars';

const Container = styled.article`
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  text-align: left;

  border-top: 1px solid ${props => props.theme.colors.black};
  padding: 30px 0;

  ${desktop`
    flex-direction: ${props => (props.isSet ? 'column-reverse' : 'row')};
  `};
`;

const Content = styled.div`
  order: 1;

  ${desktop`
    flex: 1;
    order: 2;

    margin-left: ${props => (props.isSet ? 0 : props.theme.spacing.moderate)}px;
  `};
`;

const Rating = styled.div`
  margin-bottom: ${props => props.theme.spacing.small}px;

  color: ${props => props.theme.colors.grey};
`;

const ReviewDate = styled.span`
  line-height: 1;
  vertical-align: top;

  display: flex;
  flex-direction: row;
  gap: ${props => props.theme.spacing.tiny}px;
  align-items: center;

  ${props => props.theme.paragraph.variants.paragraph3Uppercase.textStyles}

  ${StarsContainer} {
    line-height: 1;
  }
`;

const Title = styled.div`
  margin-bottom: ${props => props.theme.spacing.tiny}px;

  ${props => props.theme.paragraph.variants.paragraph1Uppercase.textStyles}
`;

const StyledParagraph = styled(Paragraph).attrs({
  size: 14,
})`
  ${desktop`
    ${props => props.theme.paragraph.variants.paragraph4.textStyles}
  `}
  ${mobile`
    ${props =>
      props.theme.paragraph.variants.paragraph4Uppercase.textStyles.textStyles}
  `}
`;

const User = styled.div`
  display: flex;
  flex-direction: column;

  color: ${props => props.theme.colors.gunmetal};
  ${props => props.theme.paragraph.variants.paragraph3Uppercase.textStyles}
  ${mobile`
    margin-bottom: 16px;
  `}
`;

const Image = styled.div`
  width: 155px;

  ${desktop`
    flex: 0 1 155px;
    order: 3;
  `};
`;

export default class ProductDetailReview extends React.PureComponent {
  static propTypes = {
    className: PropTypes.string,
    review: PropTypes.shape({
      overallRating: PropTypes.number,
      productSize: PropTypes.string,
      reviewContent: PropTypes.string,
      reviewTimestamp: PropTypes.string,
      reviewTitle: PropTypes.string,
      reviewerLocation: PropTypes.string,
      reviewerName: PropTypes.string,
    }),
    isSet: PropTypes.bool,
  };

  static defaultProps = {};
  static Rating = Rating;

  render() {
    const { className, review, isSet } = this.props;

    const initialRating =
      typeof review.overallRating === 'string'
        ? parseFloat(review.overallRating)
        : review.overallRating;

    const anonymous = (
      <FormattedMessage
        id="site_pdp.anonymous_label"
        defaultMessage="Anonymous"
      />
    );

    return (
      <Container
        className={className}
        data-autotag="user-reviews"
        isSet={isSet}
      >
        <Content isSet={isSet}>
          <Rating>
            <ReviewDate data-autotag="review-date">
              <FormattedDate
                month="long"
                day="2-digit"
                year="numeric"
                value={parseDate(review.reviewTimestamp)}
              />
              <Stars
                readonly
                initialRating={initialRating}
                // We can use the `--colors-grey` variable when the V2 theme is active (and CSS variables
                // are available), otherwise fallback to the default v1 color.
                color="var(--colors-black, #000)"
              />
            </ReviewDate>
          </Rating>
          <Title data-autotag="review-title">{review.reviewTitle}</Title>
          <StyledParagraph data-autotag="review-content">
            {review.reviewContent}
          </StyledParagraph>
        </Content>
        <User isSet={isSet}>
          <div data-autotag="pii-user-name">
            {review.reviewerName || anonymous}
          </div>
          <div>{review.reviewerLocation}</div>
          <div data-autotag="user-size">
            <FormattedMessage id="site_pdp.size_label" defaultMessage="Size" />:{' '}
            {review.productSize}
          </div>
        </User>
        {!isSet && <Image />}
      </Container>
    );
  }
}
